'use client'

import { useState } from 'react'

import { ArrowRight, ArrowUpRight } from 'lucide-react'

import type { ExpandableLinkListProps } from './types'

export default function ExpandableLinkList({ overline, links = [] }: ExpandableLinkListProps) {
	const firstImageIndex = links.findIndex((link) => link.image)
	const [activeImageIndex, setActiveImageIndex] = useState(firstImageIndex)

	if (links.length === 0) return null

	const hasImages = firstImageIndex !== -1

	/* Rows without an image leave the panel on whatever it was showing — never blank.
	   Pointer check because onMouseEnter also fires on a touch tap in some browsers. */
	function activate(index: number) {
		if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return
		if (links[index]?.image) setActiveImageIndex(index)
	}

	return (
		<section className="bg-brand-50 py-12">
			<div className="container">
				<div className="flex items-center">
					{/* ── Image panel — hidden below lg, absent when no row has an image ── */}
					{hasImages && (
						<div className="hidden lg:block flex-1 min-w-0 max-w-133">
							<figure className="relative w-full max-w-full aspect-square overflow-hidden rounded-xs">
								{links.map((link, i) =>
									link.image ? (
										<img
											key={i}
											src={link.image.src}
											alt={link.image.alt}
											className={`expandable-link-list__image absolute inset-0 w-full h-full object-cover opacity-0 transition-opacity duration-300 ${
												i === activeImageIndex ? 'expandable-link-list__image--active' : ''
											}`}
										/>
									) : null
								)}
							</figure>
						</div>
					)}

					{/* ── Text column ── */}
					<div className={`flex-1 min-w-0 pt-6 lg:pt-16 lg:pb-6 ${hasImages ? 'lg:pl-8' : ''}`}>
						<div className="flex flex-col gap-6">
							{overline && <p className="body text-black-950">{overline}</p>}

							<ul className="flex flex-col">
								{links.map((link, i) => (
									<li
										key={i}
										className="expandable-link-list__row relative border-t border-black-100 pt-[0.6875rem] pb-3 pl-3 transition-colors duration-300"
										onMouseEnter={() => activate(i)}
										onFocus={() => activate(i)}
									>
										{link.url && (
											<a
												href={link.url}
												target={link.isExternal ? '_blank' : undefined}
												rel={link.isExternal ? 'noopener noreferrer' : undefined}
												aria-label={link.label}
												className="absolute inset-0 z-10"
											/>
										)}
										<div className="flex items-center gap-3">
											<h3 className="h4 text-black-950 flex-1 min-w-0">{link.label}</h3>
											{/* size-11 box centers the 26px glyph — the box height is what makes the row 68px */}
											{link.url && (
												<span className="flex size-11 shrink-0 items-center justify-center text-black-950">
													{link.isExternal ? (
														<ArrowUpRight className="size-6.5" aria-hidden="true" />
													) : (
														<ArrowRight className="size-6.5" aria-hidden="true" />
													)}
												</span>
											)}
										</div>
										{link.description && (
											<div className="expandable-link-list__panel grid grid-rows-[0fr] opacity-0 transition-all duration-300">
												{/* min-h-0 so grid-template-rows: 0fr can actually collapse the row */}
												<p className="body text-black-950 min-h-0 overflow-hidden line-clamp-3">
													{link.description}
												</p>
											</div>
										)}
									</li>
								))}
							</ul>
						</div>
					</div>
				</div>
			</div>
		</section>
	)
}
